interface WallModeSelectorProps {
	value: "dynamic" | "static";
	onChange: (mode: "dynamic" | "static") => void;
	disabled?: boolean;
	dynamicDisabled?: boolean;
	onUpgradeClick?: () => void;
}

export default function WallModeSelector({ value, onChange, disabled = false, dynamicDisabled = false, onUpgradeClick }: WallModeSelectorProps) {
	const handleDynamicClick = () => {
		if (dynamicDisabled && onUpgradeClick) {
			onUpgradeClick();
		} else if (!disabled && !dynamicDisabled) {
			onChange("dynamic");
		}
	};

	return (
		<div>
			<div className="grid grid-cols-2 gap-3">
				{/* Dynamic tile */}
				<button
					type="button"
					onClick={handleDynamicClick}
					disabled={disabled || (dynamicDisabled && !onUpgradeClick)}
					className={`p-4 rounded-xl border-2 text-left transition-all ${
						value === "dynamic" && !dynamicDisabled ? "border-gray-900 bg-gray-50" : "border-gray-200"
					} ${disabled || (dynamicDisabled && !onUpgradeClick) ? "cursor-not-allowed" : "cursor-pointer hover:border-gray-300"}`}
				>
					<div className="flex items-center justify-between mb-1">
						<span className={`font-medium ${dynamicDisabled ? "text-gray-400" : "text-gray-900"}`}>Dynamic</span>
						{value === "dynamic" && !dynamicDisabled && (
							<svg className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
								<path
									fillRule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
									clipRule="evenodd"
								/>
							</svg>
						)}
						{dynamicDisabled && (
							<svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
							</svg>
						)}
					</div>
					<span className={`text-sm ${dynamicDisabled ? "text-gray-400" : "text-tertiary"}`}>Live updates</span>
					{dynamicDisabled && (
						<div className="mt-2 flex items-center gap-1.5">
							<span className="text-xs text-blue-600">Upgrade to enable</span>
						</div>
					)}
				</button>

				{/* Static tile */}
				<button
					type="button"
					onClick={() => !disabled && onChange("static")}
					disabled={disabled}
					className={`p-4 rounded-xl border-2 text-left transition-all ${value === "static" ? "border-gray-900 bg-gray-50" : "border-gray-200 hover:border-gray-300"} ${
						disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
					}`}
				>
					<div className="flex items-center justify-between mb-1">
						<span className="font-medium text-gray-900">Static</span>
						{value === "static" && (
							<svg className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
								<path
									fillRule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
									clipRule="evenodd"
								/>
							</svg>
						)}
					</div>
					<span className="text-sm text-tertiary">Manually curated</span>
				</button>
			</div>

			{/* Helper text */}
			<p className="text-sm text-tertiary mt-3">
				{value === "dynamic" && !dynamicDisabled
					? "Dynamic walls automatically update when new testimonials match your filters."
					: value === "dynamic" && dynamicDisabled
						? "Dynamic walls automatically update with your latest reviews. Upgrade to Growth to enable this feature."
						: "Static walls show only testimonials you select."}
			</p>
		</div>
	);
}
